import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AddRadioPort,
    DeleteUserRadio,
    Radio,
    RadioCreate,
    RadioDelete,
    RadioEdit,
    RadioListParams, RadioPort, RadioPortDisable,
    RadioWithPage, UserRadio,
} from '@app/interfaces/rm/radio';
import { ResourceField } from '@app/interfaces/rm/resources';
import { Account } from '@app/interfaces/sys/account';
import { OptionsParams, RemoteOptionWithPage, RemoteParams, SelectOption } from '@app/interfaces/sys/dict';
import { environment } from '@env/environment';
import { lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class RadioHttp {
    private moduleApi = environment.api.services.rm;
    private baseUrl = this.moduleApi + '/radios';

    constructor(private http: HttpClient) {
    }

    async radioList(option: RadioListParams): Promise<RadioWithPage> {
        return lastValueFrom(this.http.get<RadioWithPage>(this.baseUrl, {
            params: Object.assign(option),
        }));
    }

    async options(params?: OptionsParams): Promise<SelectOption[]> {
        return lastValueFrom(this.http.get<SelectOption[]>(`${this.baseUrl}/all-options`, {
            params: params ? Object.assign(params) : {}
        }));
    }

    async radioCreate(option: RadioCreate): Promise<void> {
        return lastValueFrom(this.http.post<void>(this.baseUrl, option));
    }

    async radioEdit(option: RadioEdit): Promise<void> {
        return lastValueFrom(this.http.put<void>(this.baseUrl, option));
    }

    async radioDelete(option: RadioDelete): Promise<void> {
        return lastValueFrom(this.http.request<void>('DELETE', this.baseUrl, {
            body: Object.assign(option),
        }));
    }

    async radioPortDesc(option: RadioEdit): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/radio-ports`, option));
    }

    async radioOneDetail(option: { id: number }): Promise<any> {
        return lastValueFrom(this.http.get<Radio>(`${this.baseUrl}/one`, {
            params: Object.assign(option),
        }));
    }

    async addRadioUser(option: { id: number }): Promise<Account[]> {
        return lastValueFrom(this.http.get<Account[]>(`${this.moduleApi}/user-radios/candidate-users`, {
            params: Object.assign(option),
        }));
    }

    async radioUser(option: UserRadio): Promise<any> {
        return lastValueFrom(this.http.post<any>(`${this.moduleApi}/user-radios`, option));
    }

    async deleteRadioUser(option: DeleteUserRadio): Promise<void> {
        return lastValueFrom(this.http.request<void>('DELETE', `${this.moduleApi}/user-radios`, {
            body: Object.assign(option),
        }));
    }

    async radioPortsDisable(option: RadioPortDisable): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/radio-ports/switchDisable`, option));
    }

    async userRadios(option: { id: number }): Promise<any> {
        return lastValueFrom(this.http.get<Radio>(`${this.moduleApi}/user-radios/port-users`, {
            params: Object.assign(option),
        }));
    }

    async radioOptions(params: RemoteParams): Promise<RemoteOptionWithPage> {
        return lastValueFrom(this.http.get<RemoteOptionWithPage>(`${this.moduleApi}/radio-ports/options`, {
            params: Object.assign(params)
        }));
    }

    async radioPortDelete(option: RadioDelete): Promise<void> {
        return lastValueFrom(this.http.request<void>('DELETE', `${this.moduleApi}/radio-ports`, {
            body: Object.assign(option),
        }));
    }

    async addRadioPort(params: AddRadioPort): Promise<RadioPort> {
        return lastValueFrom(this.http.post<RadioPort>(`${this.moduleApi}/radio-ports`, params));
    }

    getRadioOtherDefaultFields(): ResourceField[] {
        return [
            {
                label: 'Radio Power (W)',
                key: 'radioPower',
                width: '160px',
                type: 'input_number',
                sort: true
            },
            {
                label: 'Manufacturer',
                key: 'made',
                type: 'input',
                width: '140px'
            },
            {
                label: 'Model/Part No',
                key: 'modelPartNo',
                type: 'input',
                width: '150px'
            },
            {
                label: 'SN',
                key: 'sn',
                type: 'input',
                required: true,
                width: '50px'
            },
            {
                label: 'Qty',
                key: 'qty',
                type: 'input_number',
                width: '50px'
            },
            {
                label: 'Carrier Capacity',
                key: 'carrierCapacity',
                type: 'input',
                width: '200px'
            },
            {
                label: 'Bandwidth',
                key: 'bandwidth',
                type: 'input',
                width: '150px'
            },
            {
                label: 'MIMO',
                key: 'mimo',
                type: 'input',
            },
            {
                label: 'Technology',
                key: 'technology',
                type: 'autocomplete',
                width: '150px',
                options: [
                    {
                        value: 'UMTS',
                        label: 'UMTS',
                    },
                    {
                        value: 'LTE',
                        label: 'LTE',
                    },
                    {
                        value: '5G',
                        label: '5G',
                    },
                    {
                        value: 'NR',
                        label: 'NR',
                    },
                ],
            },
            {
                label: 'Slot',
                key: 'slot',
                type: 'autocomplete',
                width: '80px',
                options: [
                    {
                        value: 'A',
                        label: 'A',
                    },
                    {
                        value: 'B',
                        label: 'B',
                    },
                    {
                        value: 'C',
                        label: 'C',
                    },
                ],
            },
            {
                label: 'Status',
                key: 'status',
                type: 'autocomplete',
                options: [
                    {
                        value: 'Installed',
                        label: 'Installed',
                    },
                ],
            },
            {
                label: 'Purchasing order',
                key: 'telusPo',
                type: 'input',
            },
            {
                label: 'Vendor order',
                key: 'vendorOrder',
                width: '120px',
                type: 'input',
            },
        ];
    }
}
